import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export const inputCls = "w-full border border-navy/15 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-magenta outline-none bg-white";
export const btnPrimary = "inline-flex items-center justify-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-2.5 px-5 rounded-md transition-colors disabled:opacity-50";
export const btnOutline = "inline-flex items-center justify-center gap-2 border-2 border-navy text-navy hover:bg-navy-light font-bold py-2 px-4 rounded-md transition-colors disabled:opacity-50";
export const btnLink = "inline-flex items-center gap-1 text-xs font-bold text-navy hover:text-magenta transition-colors disabled:opacity-40";

export const fmt = (v) => {
  if (!v) return "—";
  const d = new Date(v.length <= 10 ? `${v}T12:00:00` : v);
  if (isNaN(d)) return v;
  return v.length <= 10 ? d.toLocaleDateString() : d.toLocaleString();
};

export const Card = ({ title, action, children, testid, className = "" }) => (
  <div className={`bg-white rounded-lg border border-navy/10 shadow-sm p-5 ${className}`} data-testid={testid}>
    {(title || action) && (
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        {title && <h3 className="font-display font-bold text-navy">{title}</h3>}
        {action}
      </div>
    )}
    {children}
  </div>
);

const STATE_CLS = {
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Signed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Passed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Pass: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Overdue: "bg-red-50 text-red-700 border-red-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
  Failed: "bg-red-50 text-red-700 border-red-200",
  Fail: "bg-red-50 text-red-700 border-red-200",
  "Replacement Requested": "bg-amber-50 text-amber-800 border-amber-200",
  "Pending Review": "bg-amber-50 text-amber-800 border-amber-200",
  Upcoming: "bg-navy-light text-navy border-navy/20",
};

export const StateBadge = ({ state, testid }) => (
  <span data-testid={testid} className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide border ${STATE_CLS[state] || "bg-soft-gray text-dark-text/70 border-navy/10"}`}>
    {state || "Open"}
  </span>
);

export const ReadMore = ({ summary, description, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-sm text-dark-text/75">
      {summary && <p>{summary}</p>}
      {description && (
        <>
          {open && <p className="mt-2 whitespace-pre-wrap bg-soft-gray rounded-md p-3" data-testid={`${testid}-full`}>{description}</p>}
          <button type="button" onClick={() => setOpen(!open)} className={`${btnLink} mt-1`} data-testid={`${testid}-toggle`}>
            {open ? <>Show less <ChevronUp size={14} /></> : <>Read more <ChevronDown size={14} /></>}
          </button>
        </>
      )}
    </div>
  );
};

export const Empty = ({ children }) => <p className="text-sm text-dark-text/50 py-4">{children}</p>;

export const Label = ({ children }) => <p className="text-xs text-dark-text/60 font-semibold mb-1">{children}</p>;

export const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm text-navy cursor-pointer select-none">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="accent-magenta w-4 h-4" />
    {label}
  </label>
);
